/**
 * The `/watch` screen's persisted state (§12.4) and its translation into #71 params.
 *
 * `/watch` is `/feed` narrowed to watch-caught posts, so its state *is* the feed's — same
 * vocabulary, same defaults, same sanitizer, stored under its own key (`watch:main`). Everything
 * here delegates: duplicating the filter arithmetic would be two chances for the two screens to
 * disagree about what a filter means. What is added is the `/saved` search tail: `q` is
 * session-only, so it arrives as an argument rather than out of the stored blob.
 */

import type { ViewRangeState, WatchFeedQuery, WatchFiltersState, WatchUiState } from '@lw/shared';
import type { ResolvedRange, ViewSort } from '../view/viewState';
import { DEFAULT_FEED_STATE, sanitizeFeedState, toFeedParams } from '../feed/feedState';

export type WatchState = {
  range: ViewRangeState;
  filters: WatchFiltersState;
  sort: ViewSort;
};

/** Feed's defaults, deliberately: last 30 days, `publishedAt` desc (§19). */
export const DEFAULT_WATCH_STATE: WatchState = {
  range: { ...DEFAULT_FEED_STATE.range },
  filters: {},
  sort: { ...DEFAULT_FEED_STATE.sort },
};

/** Stored UI state is user-writable JSON: never trust its shape, always fall back to defaults. */
export function sanitizeWatchState(raw: unknown): WatchState {
  return sanitizeFeedState(raw);
}

/** What actually goes to `PUT /api/ui-state/watch:main` (the shape of `WatchUiStateSchema`). */
export function toStoredWatchState(state: WatchState): WatchUiState {
  return { range: state.range, filters: state.filters, sort: state.sort };
}

/** #71 request params: #51's, plus the trimmed search term when there is one. */
export function toWatchParams(
  state: WatchState,
  resolved: ResolvedRange,
  search: string,
): Partial<WatchFeedQuery> {
  const params: Partial<WatchFeedQuery> = toFeedParams(state, resolved);
  const trimmed = search.trim();
  if (trimmed.length > 0) params.q = trimmed;
  return params;
}
